import React, { useEffect } from 'react';
import { FormUtils, useFormContext } from '../../../../components/molecules/Form';
import FormsRow, { FormsRowModifier } from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import Btn, { BtnModifier } from '../../../../components/molecules/Btn';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { UploadPreviewGallery } from '../Galleries/UploadPreviewGallery';
import {
  FileUpload,
  SelectedImage,
  useImageUpload,
  FileUploadResponseErrors,
  isErrorResponse,
  SelectedFile,
} from '../../../../_lib/FileUpload';
import {
  ImageUploadHeadline,
  ImageUploadDescription,
  ImageUploadValidationErrors,
} from '../../../../_lib/components/ImageUpload';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Strong } from '../../../../components/atoms/Strong/Strong';

type FormImageGalleryUploadProps = {
  id: string;
};

export default function UploadForm({ id }: FormImageGalleryUploadProps) {
  const { t } = useSonTranslation('ImageGallery');
  const { inputs, status, getStatus, hasChanges, response, reset: resetForm } = useFormContext();
  const fileUploadId = `image-gallery-upload-${id}`;
  const uploadPreviewGallery = `upload-preview-gallery-${id}`;
  const { imageuploadparams } = inputs;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore -- typing of form sucks
  const { image } = imageuploadparams.params;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore -- form types suck
  const { constraints } = image;
  const { inputProps, labelProps, selection, removeFile, isReadingFiles, reset } = useImageUpload({
    id: fileUploadId,
    input: image,
    onChange: (images: SelectedImage[]) =>
      imageuploadparams.onChange(
        image.name,
        images.filter(img => img.errors.length === 0).map(img => img.file),
      ),
  });
  const hasSubmittableImages = selection.some(i => i.errors.length === 0);

  useEffect(() => {
    // when a Submit is partially successful, the response tells us which images failed.
    // here we remove the files that where successful
    if (status === InteractionStatus.FAIL && response && Array.isArray(response.data)) {
      const errors = response.data.filter(isErrorResponse);

      const hasNoError = ({ file }: SelectedFile) => !!errors.find(error => error.originalFilename === file.name);

      const handle = setTimeout(() => removeFile(hasNoError), 2000);
      return () => {
        clearTimeout(handle);
      };
    }
    // on success, we reset the model to allow uploading the next files
    if (status === InteractionStatus.SUCCESS) {
      const handle = setTimeout(() => {
        resetForm();
        reset();
      }, 2000);
      return () => clearTimeout(handle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to track changes of status
  }, [status]);

  const statusMessages = {
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('uploadSuccess')),
    [InteractionStatus.FAIL]: [FormUtils.createStatusMessage(t('uploadFail'))],
    [InteractionStatus.SENDING]: FormUtils.createStatusMessage(
      <>
        {t('uploadInProgress')}
        <Strong> {t(`phrases:pleaseWait`)}</Strong>
      </>,
    ),
  };

  return (
    <div className="image-gallery__upload">
      <ImageUploadHeadline>{t('headline')}</ImageUploadHeadline>
      <ImageUploadDescription constraints={constraints} />
      <FileUpload labelTitle={t('buttons.chooseFile')} inputProps={inputProps} labelProps={labelProps}>
        <ImageUploadValidationErrors id={id} errors={selection.flatMap(({ errors }) => errors)} />
        <div className="image-gallery__gallery-container">
          <UploadPreviewGallery
            id={uploadPreviewGallery}
            status={status}
            selectedImages={selection}
            removeImage={removeFile}
            showSpinner={isReadingFiles}
          />
        </div>
        <FormsRow modifier={[FormsRowModifier.RIGHT, FormsRowModifier.NO_MARGIN]}>
          <Btn
            type="submit"
            modifier={[BtnModifier.SINGLE_ACTION_YES, BtnModifier.TRANSPARENT]}
            text={t('upload')}
            ariaDisabled={
              status === InteractionStatus.SUCCESS ||
              status === InteractionStatus.SENDING ||
              !hasChanges() ||
              !hasSubmittableImages
            }
          />
        </FormsRow>
        {FormUtils.renderHiddenFields(imageuploadparams.params)}
        <LiveRegion invisible={false} htmlTag="div">
          {getStatus(statusMessages, status)}
          <FileUploadResponseErrors
            status={status}
            response={response}
            maxWidth={constraints.maxWidth}
            maxHeight={constraints.maxHeight}
          />
        </LiveRegion>
      </FileUpload>
    </div>
  );
}
